import { FC, useCallback, useState } from 'react'

import { toast } from '@inno-ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { ErrorAlert } from 'modules/auth/components'
import { AuthButton } from 'modules/auth/components/AuthButton'
import {
  AuthButtonColor,
  AuthButtonVariant
} from 'modules/auth/components/AuthButton/AuthButton.constant'
import { AuthInput } from 'modules/auth/components/AuthInput'
import { useTranslation } from 'react-i18next'
import {
  CHECK_TOKEN_QUERY_KEY,
  useLoginMutation
} from 'shared/api/auth/requests'
import { useGuestDisableTwoFactorByCode } from 'shared/api/auth/requests/2fa/guestDisableTwoFactor'
import { CustomEvents } from 'shared/constants/customEvents'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { SessionStorageKey } from 'shared/constants/sessionStorage'
import { DEFAULT_REMEMBER_ME } from 'shared/constants/user'
import { useUserContext } from 'shared/hooks'
import { persistTwoFaChecked } from 'shared/utils'
import { dispatchCustomEvent } from 'shared/utils/customEvents'

import styles from './DeactivateTwoFa.module.scss'

export const DeactivateTwoFa: FC = () => {
  const [deactivationCode, setDeactivationCode] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { user } = useUserContext()

  const { t } = useTranslation('auth', {
    keyPrefix: 'new_login.login.deactivate_two_fa'
  })
  const queryClient = useQueryClient()

  const loginMutation = useLoginMutation({
    onSuccess: data => {
      toast.success({
        title: t('success.title'),
        subTitle: t('success.message')
      })
      dispatchCustomEvent(CustomEvents.AUTH_TOKEN_UPDATED, {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        redirectRoute: '/'
      })
      sessionStorage.removeItem(SessionStorageKey.OTP_LOGGING_IN_EMAIL)
      sessionStorage.removeItem(SessionStorageKey.OTP_LOGGING_IN_PASSWORD)
      if (FEATURE_FLAGS.SHOW_REMEMBER_ME) {
        sessionStorage.removeItem(SessionStorageKey.OTP_LOGGING_IN_REMEMBER_ME)
      }
      queryClient.invalidateQueries({
        queryKey: [CHECK_TOKEN_QUERY_KEY],
        exact: false
      })
    }
  })

  const onSuccess = useCallback(() => {
    // The login below immediately re-issues fresh tokens (and reflects the
    // disabled state correctly), so skip the util's own token refresh here —
    // running both would race the same refresh token being rotated twice.
    persistTwoFaChecked(user, 0, { skipTokenRefresh: true })
    const email =
      sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_EMAIL) ?? ''
    const password =
      sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_PASSWORD) ?? ''
    const remember_me = FEATURE_FLAGS.SHOW_REMEMBER_ME
      ? sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_REMEMBER_ME) ===
        'true'
      : DEFAULT_REMEMBER_ME

    loginMutation.mutate({
      email,
      password,
      remember_me
    })
  }, [loginMutation, user])

  const onError = useCallback(() => {
    setErrorMessage(t('error.message'))
    setDeactivationCode('')
  }, [t])

  const {
    mutation: {
      mutate: guestDisableTwoFactorMutate,
      isPending: isLoadingDeactivate
    }
  } = useGuestDisableTwoFactorByCode({ onSuccess, onError })

  const isLoading = isLoadingDeactivate || loginMutation.isPending

  const isFormValid = deactivationCode.trim().length > 0

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (isFormValid) {
        const email =
          sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_EMAIL) ?? ''
        const password =
          sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_PASSWORD) ??
          ''
        guestDisableTwoFactorMutate({
          email,
          password,
          code: deactivationCode
        })
      }
    },
    [deactivationCode, guestDisableTwoFactorMutate, isFormValid]
  )

  return (
    <section className={styles.deactivateTwoFa}>
      <main className={styles.content}>
        <header className={styles.header}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.description}>{t('description')}</p>
        </header>
        <form onSubmit={handleSubmit} className={styles.form}>
          <AuthInput
            placeholder={t('placeholder')}
            value={deactivationCode}
            onChange={e => {
              setDeactivationCode(e.target.value)
              setErrorMessage('')
            }}
          />
          <ErrorAlert message={errorMessage} />

          <AuthButton
            className={styles.submitButton}
            label={t('button_text')}
            variant={AuthButtonVariant.FILLED_BLUE}
            colorType={AuthButtonColor.GHOST_DARKER}
            isLoading={isLoading}
            fullWidth={true}
            isBold
            disabled={!isFormValid}
            type='submit'
          />
        </form>
      </main>
    </section>
  )
}
